import React from "react";
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Container,
  Box,
  Button,
} from "@mui/material";
import useGlobalState from "../../Context";
import { useState } from "react";
import { VENDOR_FC_LISTING_COLUMNS } from "../../utils/vendorProfileConstant";
import AddFC from "./AddFC";
import { useEffect } from "react";
import NoDataComponent from "../../common/RecordNotFound";

import {
  addVendorFcMappingService,
  getVendorFcMasterListService,
  getVendorFcService,
  toggleVendorFcMappingService,
} from "../../services/vendorProfileServices";

import { useLoading } from "../../common/LoadingContext";
import { useNotificationContext } from "../../common/NotificationContext";
import ConfirmationAlert from "./ConfirmationAlert";
import { padPO } from "../../utils/jsUtils";

const VendorFcList = () => {
  const [{ userDetails }] = useGlobalState();
  const userParam = userDetails?.userParam;
  const vendorCode = userParam?.vendorCode;

  const [vendorFcList, setVendorFcList] = useState([]);
  const { setLoading } = useLoading();
  const { showNotification } = useNotificationContext();
  const [fcListOptions, setFcListOptions] = useState([]);
  const [recordToRemove, setRecordToRemove] = useState(null);
  const [recordsCount, setRecordsCount] = useState();

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const handleDeleteConfirmationOpen = () => {
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteConfirmationOpen(false);
  };
  const handleRecordDelete = async () => {
    try {
      setLoading(true);
      const requestObj = {
        vendorCode: padPO(vendorCode),
        fcCode: recordToRemove?.fcCode,
      };
      const removeVendorResponse = await toggleVendorFcMappingService(
        baseUrl,
        requestObj
      );
      if (removeVendorResponse.messageCode === 6) {
        const newVendorFcList = vendorFcList.map((fcItem) => {
          if (fcItem?.fcCode === recordToRemove?.fcCode) {
            return { ...fcItem, status: 0 };
          } else {
            return fcItem;
          }
        });
        await fetchFCList();
        showNotification(
          "Success",
          removeVendorResponse?.message ?? "FC Removed Successfully",
          "success"
        );
        setVendorFcList(newVendorFcList);
        setRecordsCount((prev) => prev - 1);
      } else {
        showNotification(
          "Error",
          removeVendorResponse?.message ?? "Failed to Remove FC"
        );
      }
    } catch (err) {
      showNotification("Error", err?.message ?? "Failed to Remove FC");
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorFC = async () => {
    try {
      setLoading(true);

      const requestBody = {
        vendorCode: padPO(vendorCode),
      };
      const vendorFcResponse = await getVendorFcService(baseUrl, requestBody);
      if (vendorFcResponse?.messageCode === 6) {
        setVendorFcList(vendorFcResponse.vendorUserFcMappingParamList);
        setRecordsCount(vendorFcResponse?.activeCount);
      } else {
        setVendorFcList([]);
        setRecordsCount(0);
        showNotification(
          "Error",
          vendorFcResponse?.message ?? "Failed to Fetch Vendor FC List"
        );
      }
    } catch (err) {
      showNotification(
        "Error",
        err?.message ?? "Failed to Fetch Vendor FC List"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchFCList = async () => {
    try {
      setLoading(true);
      const requestBody = {
        vendorCode: padPO(vendorCode),
      };
      const fcListResponse = await getVendorFcMasterListService(
        baseUrl,
        requestBody
      );
      if (fcListResponse.messageCode === 6) {
        setFcListOptions(fcListResponse?.fcMasterParamList);
      } else {
        showNotification(
          "Error",
          fcListResponse?.message ?? "Failed to Fetch FC List"
        );
      }
    } catch (err) {
      showNotification("Error", err?.message ?? "Failed to Fetch FC List");
    } finally {
      setLoading(false);
    }
  };

  const addVendorFc = async (selectedFc) => {
    try {
      setLoading(true);
      const requestBody = {
        vendorCode: padPO(vendorCode),
        fcCode: selectedFc?.fcCode,
      };
      const addVendorResponse = await addVendorFcMappingService(
        baseUrl,
        requestBody
      );
      if (addVendorResponse?.messageCode === 6) {
        showNotification(
          "Success",
          `FC ${selectedFc?.fcName} Added Successfully`,
          "success"
        );
        vendorFcList.push(selectedFc);
        setRecordsCount((prev) => prev + 1);
        await fetchFCList();
      } else {
        showNotification(
          "Error",
          addVendorResponse?.message ?? "Failed to Add FC List"
        );
      }
    } catch (err) {
      showNotification("Error", err?.message ?? "Failed to Add FC List");
    } finally {
      setLoading(false);
    }
  };

  const initialLoadAPI = async () => {
    await fetchVendorFC();
    await fetchFCList();
  };
  useEffect(() => {
    initialLoadAPI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      <Container maxWidth="xl" sx={{ mt: 8, px: 1 }} disableGutters>
        <Grid container spacing={1} sx={{ pl: 3, pr: 3 }} display="flex">
          <Grid item xs={12} md={4} textAlign="left" alignSelf={"center"}>
            <div className="pageHeader">FC List (For Query Support)</div>
          </Grid>
          <Grid item xs={12} md={8} textAlign={"right"}>
            <AddFC fcListOptions={fcListOptions} addVendorFc={addVendorFc} />
          </Grid>
        </Grid>
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={12} mb={5} sx={{ pl: 3, pr: 3, mt: 2 }}>
            {vendorFcList?.length > 0 ? (
              <Paper elevation={3} variant="elevation" sx={{ width: "100%" }}>
                <TableContainer sx={{ maxHeight: 720 }} className="tableScroll">
                  <Table stickyHeader aria-label="sticky table">
                    <TableHead>
                      <TableRow key={"1"}>
                        {VENDOR_FC_LISTING_COLUMNS.map((column) => (
                          <TableCell
                            className="tableHeader"
                            size="small"
                            variant="head"
                            key={column.id}
                            sx={{ pt: 1.5, pb: 1.5, fontSize: "13px" }}
                          // style={{
                          //   backgroundColor: "#eeeeee",
                          //   color: "#003A6B",
                          // }}
                          >
                            {column.label}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>

                    <TableBody sx={{ mt: 2 }}>
                      {vendorFcList
                        .slice(
                          page * rowsPerPage,
                          page * rowsPerPage + rowsPerPage
                        )
                        .map((row, index) => {
                          return row.status !== 0 ? (
                            <TableRow hover tabIndex={-1} key={index}>
                              <TableCell
                                size="small"
                                sx={{
                                  pt: 1.5,
                                  pb: 1.5,
                                  fontSize: "12px",
                                }}
                                key={"fcCode"}
                              >
                                {row["fcCode"]}
                              </TableCell>
                              <TableCell
                                size="small"
                                sx={{
                                  pt: 1.5,
                                  pb: 1.5,
                                  fontSize: "12px",
                                }}
                                key={"fcName"}
                              >
                                {row["fcName"]}
                              </TableCell>

                              <TableCell>
                                <Button
                                  onClick={() => {
                                    setRecordToRemove(row);
                                    handleDeleteConfirmationOpen();
                                  }}
                                >
                                  <Box sx={{ display: "flex" }}>Remove</Box>
                                </Button>
                              </TableCell>
                            </TableRow>
                          ) : (
                            <TableRow
                              hover
                              tabIndex={-1}
                              key={index}
                            ></TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 100]}
                  component="div"
                  count={recordsCount ?? 0}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </Paper>
            ) : (
              <Paper>
                <NoDataComponent />
              </Paper>
            )}
            <ConfirmationAlert
              open={deleteConfirmationOpen}
              handleClose={handleDeleteConfirmationClose}
              handleRecordDelete={handleRecordDelete}
            />
          </Grid>
        </Grid>
      </Container>
    </>
  );
};
export default VendorFcList;
